const trimCell = (value) => String(value || '').trim().replace(/^"|"$/g, '');

const parseDelimitedLine = (line, delimiter) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      cells.push(trimCell(current));
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(trimCell(current));
  return cells;
};

const splitCellsForLine = (line) => {
  const byComma = parseDelimitedLine(line, ',');
  const bySemi = parseDelimitedLine(line, ';');
  if (byComma.length >= 3) return { delimiter: ',', parts: byComma };
  if (bySemi.length >= 3) return { delimiter: ';', parts: bySemi };
  if (byComma.length > bySemi.length) return { delimiter: ',', parts: byComma };
  return { delimiter: ';', parts: bySemi };
};

const quoteCsvCell = (value) => {
  const str = String(value || '');
  if (/[",\n\r;]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

const splitInvitationLine = (line) => {
  const { delimiter, parts } = splitCellsForLine(line);
  if (parts.length < 3) return null;
  const employeeName = parts[0];
  const employeeEmail = parts[1];
  const location = parts.length === 3 ? parts[2] : parts.slice(2).join(delimiter);
  return { employeeName, employeeEmail, location: location || '' };
};

const isHeaderRow = (line) => {
  const { parts } = splitCellsForLine(line);
  if (parts.length < 3) return false;
  const third = parts[2].replace(/\s+/g, '').toLowerCase();
  return (
    /^name$/i.test(parts[0]) &&
    /^email$/i.test(parts[1]) &&
    (third === 'location' ||
      third === 'locationname' ||
      third === 'officelocation' ||
      third === 'facilityname' ||
      third === 'facilityid')
  );
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateInvitationRow = (row) => {
  if (!row) {
    return {
      valid: false,
      reason: 'Each row needs name, email, and office location (comma- or semicolon-separated, e.g. Excel locale CSV).',
    };
  }
  if (!row.employeeName || !row.employeeEmail) {
    return { valid: false, reason: 'Missing name or email.' };
  }
  if (!EMAIL_RE.test(row.employeeEmail)) {
    return { valid: false, reason: 'Invalid email address.' };
  }
  if (!String(row.location || '').trim()) {
    return { valid: false, reason: 'Missing office location.' };
  }
  return {
    valid: true,
    row: {
      employeeName: row.employeeName,
      employeeEmail: row.employeeEmail,
      location: String(row.location).trim(),
    },
  };
};

/** Per-line parse result for quick-add preview and inline validation. */
export const getInvitationDraftLineStatuses = (text) => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return [];

  let start = 0;
  if (isHeaderRow(lines[0])) start = 1;

  const statuses = [];
  for (let i = start; i < lines.length; i++) {
    const lineNum = i + 1;
    const raw = lines[i];
    const split = splitInvitationLine(raw);
    const validation = validateInvitationRow(split);
    statuses.push({
      lineNum,
      raw,
      employeeName: split?.employeeName || '',
      employeeEmail: split?.employeeEmail || '',
      location: split?.location || '',
      valid: validation.valid,
      reason: validation.reason,
    });
  }
  return statuses;
};

export const parseInvitationCsvText = (text) => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return { invitations: [], skipped: [{ line: 1, reason: 'File is empty' }] };
  }

  let start = 0;
  if (isHeaderRow(lines[0])) start = 1;

  const invitations = [];
  const skipped = [];

  for (let i = start; i < lines.length; i++) {
    const lineNum = i + 1;
    const validation = validateInvitationRow(splitInvitationLine(lines[i]));
    if (!validation.valid) {
      skipped.push({ line: lineNum, reason: validation.reason });
      continue;
    }
    invitations.push(validation.row);
  }

  return { invitations, skipped };
};

export const buildInvitationTemplateContent = (facilities) => {
  const header = 'name,email,location';
  const exampleLoc = facilities?.[0]?.name?.trim() || 'Your office name';
  const example = `Jane Doe,jane@example.com,${quoteCsvCell(exampleLoc)}`;
  return `\uFEFF${header}\n${example}\n`;
};

export const downloadInvitationTemplateCsv = (campaignName, facilities) => {
  const blob = new Blob([buildInvitationTemplateContent(facilities)], { type: 'text/csv;charset=utf-8;' });
  const safe = String(campaignName || 'survey-campaign')
    .replace(/[^\w.-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'survey-campaign';
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${safe}-invitations-template.csv`;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
